/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, observable } from 'mobx';
import { OmitFunctions } from 'src/core/stores/types';
import { LuisId } from 'src/luis/types/Resource';
import { PhraseListStore } from 'src/luis/types/stores/features/PhraseListStore';

export type ConversationPhraseListStoreData = OmitFunctions<Omit<ConversationPhraseListStore, 'id'>>;

export class ConversationPhraseListStore {
    public id: LuisId;

    @observable public name: string;

    @observable public phrases: string[];

    @observable public isActive: boolean;

    @observable public isExchangeable: boolean;

    @observable public isGlobal: boolean;

    constructor(id: LuisId, phraseList: Partial<ConversationPhraseListStoreData>) {
        this.id = id;
        this.name = phraseList.name;
        this.phrases = phraseList.phrases || [];
        this.isActive = phraseList.isActive;
        this.isExchangeable = phraseList.isExchangeable;
        this.isGlobal = phraseList.isGlobal;
    }

    /**
     * A function to update the name.
     * @param name New name.
     */
    @action
    public rename(name: string) {
        this.name = name;
    }

    /**
     * A function to rename the phrase list.
     * @param phrases New phrases.
     */
    @action
    public setPhrases(phrases: string[]) {
        this.phrases = phrases;
    }

    /**
     * A function to set if the phrase list is active.
     * @param isActive New value for isActive;
     */
    @action
    public setActive(isActive: boolean) {
        this.isActive = isActive;
    }

    /**
     * A function to set if the phrase list is exchangeable.
     * @param isExchangeable new value for isExchangeable.
     */
    @action
    public setExchangeable(isExchangeable: boolean) {
        this.isExchangeable = isExchangeable;
    }

    /**
     * A function to set if the phrase list is global.
     * @param isGlobal New value for isGlobal.
     */
    @action
    public setIsGlobal(isGlobal: boolean) {
        this.isGlobal = isGlobal;
    }
}

/**
 * An phrase list store factory object.
 *
 * @param id The id of the phrase list to create.
 * @param phraseList The phrase list data.
 */
export const createConversationPhraseListStore = (id: LuisId, phraseList: Partial<ConversationPhraseListStoreData>): PhraseListStore =>
    new ConversationPhraseListStore(id, phraseList);
